import { useEffect, useState, useRef } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Link } from "react-router-dom";
import { Users, Heart, Globe2, BookOpen, Calendar, Target, Flag, Sparkles, Award, Rocket, MapPin, ChevronDown, ChevronUp } from "lucide-react";
import { motion } from "framer-motion";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "../components/ui/collapsible";
import { supabase } from "../../../src/integrations/supabase/client";
import ofuesiLogo from "../assets/ofuesi-logo.png";
import christianMission from "../assets/conference-pic-2.jpg";
import christianVision from "../assets/ofuesi_vision.png";
import christianBeginnings from "../assets/conference-pic-1.jpg";
import ofuesiBeginnings from "../assets/ofuesi_apartment.png";
import PageLoader from "@/components/PageLoader";
import { useImagePreloader } from "@/hooks/useImagePreloader";
import { OptimizedImage } from "@/components/ui/OptimizedImage";

interface Member {
  id: string;
  full_name: string;
  role: string;
  notes?: string;
  profile_image_url?: string;
  regions?: { name: string };
}

const About = () => {
  const [currentMembers, setCurrentMembers] = useState<Member[]>([]);
  const [presidentMessage, setPresidentMessage] = useState<string>("");
  const [presidentName, setPresidentName] = useState<string>("Israel Nandamudi");
  const [presidentImage, setPresidentImage] = useState<string>("");
  const [loading, setLoading] = useState(true);
  const presidentMessageRef = useRef<HTMLDivElement>(null);
  const [leadershipMaxHeight, setLeadershipMaxHeight] = useState<number>(600);
  const [ecOpen, setEcOpen] = useState(true);
  const [ncOpen, setNcOpen] = useState(true);
  const [rcOpen, setRcOpen] = useState(true);
  
  const ecRef = useRef<HTMLDivElement>(null);
  const ncRef = useRef<HTMLDivElement>(null);
  const rcRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    fetchCurrentMembers();
  }, []);

  // Removed auto-collapse on scroll - manual collapse only

  const fetchCurrentMembers = async () => {
    try {
      const currentYear = new Date().getFullYear();
      // Read from sanitized public view (excludes email/phone/notes)
      const { data, error } = await (supabase as any)
        .from("members_public")
        .select("*, regions(name)")
        .or(`end_year.is.null,end_year.gte.${currentYear}`)
        .order("order_index", { ascending: true });

      if (error) throw error;

      setCurrentMembers(data || []);

      // President's name and image come from the public view; the personal
      // "notes" message is no longer publicly readable.
      const president = data?.find((member: any) => member.role === "president");
      if (president) {
        if (president.full_name) setPresidentName(president.full_name);
        if (president.profile_image_url) setPresidentImage(president.profile_image_url);
      }
    } catch (error) {
      console.error("Error fetching members:", error);
    } finally {
      setLoading(false);
    }
  };
  const milestones = [
    { year: "1999", event: "OFUESI Founded", description: "Established to support UESI ministry" },
    { year: "2010", event: "First Regional Conferences", description: "Expanded to regional structure" },
    { year: "2015", event: "Digital Outreach", description: "Launched online prayer and fellowship platforms" },
    { year: "2024", event: "Silver Jubilee", description: "Celebrating 25 years of faithful service" },
  ];

  // Dynamic leadership data from Supabase
  const [ecMembers, setEcMembers] = useState<
    Array<{ id: string; name: string; role: string; order_index: number; photo_url: string | null }>
  >([]);
  const [ncMembers, setNcMembers] = useState<
    Array<{ id: string; name: string; order_index: number; photo_url: string | null }>
  >([]);
  const [coordinators, setCoordinators] = useState<
    Array<{
      id: string;
      name: string;
      email: string | null;
      phone: string | null;
      region_id: string;
      photo_url: string | null;
      regionName: string;
    }>
  >([]);

  const roleLabel: Record<string, string> = {
    president: "President",
    vice_president: "Vice President",
    secretary: "Secretary",
    treasurer: "Treasurer",
    prayer_secretary: "Prayer Secretary",
    prayer_joint_secretary: "Prayer/Joint Secretary",
    ex_officio: "Ex-Officio",
    national_council: "National Council",
  };

  const regionalRoleLabel: Record<string, string> = {
    regional_president: "Regional President",
    regional_vice_president: "Regional Vice-President",
    regional_secretary: "Regional Secretary",
    regional_treasurer: "Regional Treasurer",
    regional_joint_secretary: "Regional Joint Secretary",
    regional_prayer_secretary: "Regional Prayer Secretary",
  };

  useEffect(() => {
    document.title = "About OFUESI – Leadership & Mission";
    const desc = "OFUESI leadership: Executive Committee, National Council, and 6 Regional Coordinators.";
    let meta = document.querySelector('meta[name="description"]');
    if (!meta) {
      meta = document.createElement("meta");
      meta.setAttribute("name", "description");
      document.head.appendChild(meta);
    }
    meta.setAttribute("content", desc);

    // Canonical
    let link = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!link) {
      link = document.createElement("link");
      link.rel = "canonical";
      document.head.appendChild(link);
    }
    link.href = window.location.href;
  }, []);

  // Dynamic height matching for Leadership section
  useEffect(() => {
    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setLeadershipMaxHeight(entry.contentRect.height);
      }
    });

    if (presidentMessageRef.current) {
      resizeObserver.observe(presidentMessageRef.current);
    }

    return () => resizeObserver.disconnect();
  }, [presidentMessage, loading]);

  // Preload critical section images only for page load gate
  const criticalImageUrls = [
    ofuesiBeginnings,
    christianVision,
    christianMission,
  ];

  const { imagesLoaded: criticalLoaded, preloadedImages: criticalPreloadedImages } = useImagePreloader({ 
    imageUrls: criticalImageUrls, 
    enabled: !loading 
  });

  useEffect(() => {
    const load = async () => {
      try {
        const [leadersResult, coordinatorsFromRolesResult, regionsResult] = await Promise.all([
          (supabase as any).from("members_public").select("*").order("order_index"),
          // Get coordinators from member_roles table to support multiple roles
          // Public view excludes email/phone — visitors should use the contact form
          (supabase as any)
            .from("member_roles")
            .select(
              `
              role,
              member_id,
              members_public!inner(
                id,
                full_name,
                profile_image_url,
                region_id,
                is_active,
                order_index,
                regions(name, color)
              )
            `,
            )
            .in("role", [
              "regional_president",
              "regional_vice_president",
              "regional_secretary",
              "regional_joint_secretary",
              "regional_treasurer",
              "regional_prayer_secretary",
            ])
            .eq("members_public.is_active", true),
          supabase.from("regions").select("*").order("name"),
        ]);

        if (leadersResult.error) throw leadersResult.error;
        if (coordinatorsFromRolesResult.error) throw coordinatorsFromRolesResult.error;
        if (regionsResult.error) throw regionsResult.error;

        const lm = leadersResult.data ?? [];
        setEcMembers(
          lm
            .filter((m: any) => m.role !== "national_council")
            .sort((a: any, b: any) => (a.order_index ?? 0) - (b.order_index ?? 0)),
        );
        setNcMembers(
          lm
            .filter((m: any) => m.role === "national_council")
            .sort((a: any, b: any) => (a.order_index ?? 0) - (b.order_index ?? 0)),
        );

        const regionsList = regionsResult.data ?? [];

        const regionMap = new Map(
          regionsList.map((r: any) => [
            r.id,
            {
              name: r.name,
              color: r.color,
            },
          ]),
        );

        // Role priority for sorting
        const rolePriority: Record<string, number> = {
          regional_president: 1,
          regional_vice_president: 2,
          regional_secretary: 3,
          regional_joint_secretary: 4,
          regional_treasurer: 5,
          regional_prayer_secretary: 6,
        };

        // Transform coordinator data from member_roles
        const coordsFromRoles = (coordinatorsFromRolesResult.data ?? []).map((item: any) => {
          const member = item.members_public;
          return {
            ...member,
            role: item.role,
            regionName: member.regions?.name || regionMap.get(member.region_id)?.name || "Region",
            regionColor: member.regions?.color || regionMap.get(member.region_id)?.color || "#8B5CF6",
          };
        });

        // Sort coordinators by role priority
        const sortedCoordinators = coordsFromRoles.sort(
          (a: any, b: any) => (rolePriority[a.role] || 999) - (rolePriority[b.role] || 999),
        );

        console.log("Coordinators loaded from member_roles:", sortedCoordinators.length);
        console.log(
          "Coordinators by region:",
          sortedCoordinators.reduce((acc: any, coord: any) => {
            const region = coord.regionName.replace(/ Region$/i, "");
            acc[region] = (acc[region] || 0) + 1;
            return acc;
          }, {}),
        );

        setCoordinators(sortedCoordinators);
      } catch (error) {
        console.error("Error loading data:", error);
      }
    };
    load();
  }, []);

  if (loading || !criticalLoaded) {
    return <PageLoader message="Loading about page..." />;
  }

  return (
    <div className="min-h-screen faith-pattern" style={{ background: "var(--background)" }}>
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-ministry-cyan via-primary to-ministry-cyan py-10 relative">
        <div className="absolute inset-0 faith-pattern opacity-20"></div>
        <div className="container mx-auto px-4 text-center relative z-10">
          <div className="max-w-4xl mx-auto text-white space-y-6">
            <h1 className="text-4xl lg:text-6xl font-bold text-white drop-shadow-lg">Our Story & Mission</h1>
            <p className="text-xl leading-relaxed text-white">
              Serving university students across America since 1999, supporting spiritual growth and ministry
              partnerships with UESI in India.
            </p>
          </div>
        </div>
      </section>
      {/* Mission, Vision & Beginnings */}
      <section className="py-20 relative faith-pattern">
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-primary/5 to-transparent"></div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="space-y-16">
            {/* Our Beginnings */}
            <div className="flex flex-col lg:flex-row items-center gap-12">
              <div className="lg:w-1/2">
                <OptimizedImage
                  src={ofuesiBeginnings}
                  alt="OFUESI conference group photo"
                  preloadedSrc={criticalPreloadedImages.get(ofuesiBeginnings)}
                  className="w-full rounded-2xl shadow-xl"
                  aspectRatio="5/2"
                  loading="eager"
                />
              </div>
              <div className="lg:w-1/2 space-y-6">
                <h2 className="text-4xl leading-relaxed font-bold bg-gradient-to-r from-ministry-purple to-ministry-gold bg-clip-text text-transparent">
                  Our Beginnings
                </h2>
                <p className="text-lg leading-relaxed text-muted-foreground">
                  Overseas Friends of Union of Evangelical Students of India (OFUESI) is an inter-denominational
                  Christian organization formed in 1999, born in an apartment in La Mirada, California,with the purpose
                  of establishing a support base in USA for the ongoing ministry of Union of Evangelical Students of
                  India (UESI). Membership to OFUESI is voluntary and consists of those who have been blessed by the
                  ministry of UESI and those who have a heart compelled with the love of Christ for the overseas
                  students and graduates of India. Within the USA, we meet regionally for prayer, fellowship, spiritual
                  growth and support. OFUESI was first registered in 2001 as a tax exempt 501(c)3 non-profit
                  organization in the State of California, later we relocated and re-registered OFUESI-USA as a tax
                  exempt charitable organization in the State of Maryland from 2006.
                </p>
              </div>
            </div>

            {/* Our Vision */}
            <div className="flex flex-col lg:flex-row-reverse gap-12">
              <div className="lg:w-1/2">
                <OptimizedImage
                  src={christianVision}
                  alt="Student fellowship and prayer gathering"
                  preloadedSrc={criticalPreloadedImages.get(christianVision)}
                  className="w-full rounded-2xl shadow-xl"
                  aspectRatio="5/2"
                  loading="eager"
                />
              </div>
              <div className="lg:w-1/2 space-y-6">
                <h2 className="text-4xl font-bold bg-gradient-to-r from-primary to-ministry-purple bg-clip-text text-transparent">
                  Our Vision
                </h2>
                <p className="text-lg leading-relaxed text-muted-foreground">
                  Fostering the UESI vision in the US and strengthening the UESI through OFUESI
                </p>
              </div>
            </div>

            {/* Our Mission */}
            <div className="flex flex-col lg:flex-row items-center gap-12">
              <div className="lg:w-1/2">
                <OptimizedImage
                  src={christianMission}
                  alt="Christian worship and prayer"
                  preloadedSrc={criticalPreloadedImages.get(christianMission)}
                  className="w-full rounded-2xl shadow-xl"
                  aspectRatio="5/2"
                  loading="eager"
                />
              </div>
              <div className="lg:w-1/2 space-y-6">
                <h2 className="text-4xl font-bold bg-gradient-to-r from-ministry-emerald to-ministry-cyan bg-clip-text text-transparent">
                  Our Mission
                </h2>
                <p className="text-lg leading-relaxed text-muted-foreground">
                  To strengthen the bonds of fellowship among OFUESI, provide prayer and financial support to the UESI
                  work in India and expand evangelism efforts among Indian students in the USA.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* Our Journey Roadmap */}
      <section className="py-20 relative faith-pattern">
        <div className="absolute inset-0 bg-gradient-to-r from-ministry-emerald/5 via-ministry-cyan/8 to-ministry-emerald/5"></div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold mb-4 bg-gradient-to-r from-ministry-emerald via-ministry-cyan to-primary bg-clip-text text-black">
              Our Journey Roadmap
            </h2>
            <div className="inline-flex items-center gap-2 bg-gradient-to-r from-ministry-gold/10 to-ministry-purple/10 backdrop-blur-sm rounded-full px-4 py-2 border border-ministry-gold/20">
              <span className="text-lg">🏁</span>
              <span className="text-sm font-medium text-muted-foreground">
                1999 - 2028: A Legacy of Faith & Service
              </span>
              <span className="text-lg">🎯</span>
            </div>
          </div>

          <div className="max-w-5xl mx-auto">
            {/* Horizontal Timeline */}
            <div className="relative py-6">
              {/* Main Timeline Line */}
              <div className="absolute top-12 left-0 right-0 h-0.5 bg-gradient-to-r from-ministry-emerald via-ministry-gold to-ministry-purple"></div>

              {/* Timeline Items - Horizontal Layout */}
              <div className="flex justify-between items-start relative">
                {/* 1999 - Foundation */}
                <div className="flex flex-col items-center text-center max-w-28">
                  <div className="w-12 h-12 bg-gradient-to-br from-ministry-emerald to-ministry-cyan rounded-full flex items-center justify-center mb-3 shadow-lg border-2 border-white relative z-10">
                    <span className="text-sm">🏁</span>
                  </div>
                  <div className="bg-white/90 backdrop-blur-sm rounded-lg p-2 shadow-sm border border-ministry-emerald/20">
                    <div className="text-xs font-bold text-ministry-emerald mb-1">1999</div>
                    <div className="text-xs font-medium text-gray-700">Foundation</div>
                    <div className="text-xs text-gray-600 mt-1">OFUESI Born</div>
                  </div>
                </div>

                {/* 2004 - Growth */}
                <div className="flex flex-col items-center text-center max-w-28">
                  <div className="w-12 h-12 bg-gradient-to-br from-ministry-cyan to-ministry-blue rounded-full flex items-center justify-center mb-3 shadow-lg border-2 border-white relative z-10">
                    <span className="text-sm">📈</span>
                  </div>
                  <div className="bg-white/90 backdrop-blur-sm rounded-lg p-2 shadow-sm border border-ministry-cyan/20">
                    <div className="text-xs font-bold text-ministry-cyan mb-1">2004</div>
                    <div className="text-xs font-medium text-gray-700">Expansion</div>
                    <div className="text-xs text-gray-600 mt-1">Growth Phase</div>
                  </div>
                </div>

                {/* 2010 - Regional Structure */}
                <div className="flex flex-col items-center text-center max-w-28">
                  <div className="w-12 h-12 bg-gradient-to-br from-ministry-blue to-ministry-purple rounded-full flex items-center justify-center mb-3 shadow-lg border-2 border-white relative z-10">
                    <span className="text-sm">🗺️</span>
                  </div>
                  <div className="bg-white/90 backdrop-blur-sm rounded-lg p-2 shadow-sm border border-ministry-blue/20">
                    <div className="text-xs font-bold text-ministry-blue mb-1">2010</div>
                    <div className="text-xs font-medium text-gray-700">Regions</div>
                    <div className="text-xs text-gray-600 mt-1">National Scale</div>
                  </div>
                </div>

                {/* 2024 - Silver Jubilee */}
                <div className="flex flex-col items-center text-center max-w-28 relative">
                  <div className="w-12 h-12 bg-gradient-to-br from-ministry-gold via-ministry-rose to-ministry-purple rounded-full flex items-center justify-center mb-3 shadow-xl border-2 border-white relative z-10">
                    <span className="text-sm">🎉</span>
                  </div>
                  <div className="bg-gradient-to-br from-ministry-gold/20 to-ministry-purple/20 backdrop-blur-sm rounded-lg p-2 shadow-lg border-2 border-ministry-gold/30">
                    <div className="text-xs font-bold text-ministry-gold mb-1">2024</div>
                    <div className="text-xs font-bold text-ministry-purple">Silver Jubilee</div>
                    <div className="text-xs text-gray-700 mt-1">25 Years! 🎊</div>
                  </div>
                  {/* Confetti Effect */}
                  <div className="absolute -top-2 -left-2 text-xs animate-bounce">🎊</div>
                  <div className="absolute -top-1 right-1 text-xs animate-bounce delay-300">✨</div>
                </div>

                {/* 2028 - Future Vision */}
                <div className="flex flex-col items-center text-center max-w-28">
                  <div className="w-12 h-12 bg-gradient-to-br from-ministry-purple to-ministry-rose rounded-full flex items-center justify-center mb-3 shadow-lg border-2 border-white relative z-10 opacity-80">
                    <span className="text-sm">🚀</span>
                  </div>
                  <div className="bg-gradient-to-br from-ministry-purple/10 to-ministry-rose/10 backdrop-blur-sm rounded-lg p-2 shadow-sm border border-ministry-purple/20 border-dashed">
                    <div className="text-xs font-bold text-ministry-purple mb-1">2028</div>
                    <div className="text-xs font-medium text-gray-700">Next Conference</div>
                    <div className="text-xs text-gray-600 mt-1">Future Vision</div>
                  </div>
                </div>
              </div>

              {/* Summary Stats */}
              <div className="mt-8 grid grid-cols-3 gap-4 max-w-md mx-auto">
                <div className="text-center p-2 bg-gradient-to-br from-ministry-emerald/10 to-ministry-cyan/10 rounded-lg border border-ministry-emerald/20">
                  <div className="text-lg font-bold text-ministry-emerald">25+</div>
                  <div className="text-xs text-gray-600">Years</div>
                </div>
                <div className="text-center p-2 bg-gradient-to-br from-ministry-gold/10 to-ministry-purple/10 rounded-lg border border-ministry-gold/20">
                  <div className="text-lg font-bold text-ministry-gold">6</div>
                  <div className="text-xs text-gray-600">Regions</div>
                </div>
                <div className="text-center p-2 bg-gradient-to-br from-ministry-purple/10 to-ministry-rose/10 rounded-lg border border-ministry-purple/20">
                  <div className="text-lg font-bold text-ministry-purple">1000+</div>
                  <div className="text-xs text-gray-600">Students</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* Leadership & President's Message Side by Side */}
      <section className="py-20 relative faith-pattern">
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-primary/5 to-transparent"></div>
        <div className="container mx-auto px-4 relative z-10">
          {/* Remove combined heading - sections will have their own headings */}

          {loading ? (
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-8">
              {[...Array(6)].map((_, index) => (
                <div key={index} className="text-center">
                  <div className="w-24 h-24 rounded-full bg-muted mx-auto mb-3 animate-pulse"></div>
                  <div className="h-4 bg-muted rounded mx-auto w-3/4 mb-2 animate-pulse"></div>
                  <div className="h-4 bg-muted rounded mx-auto w-1/2 animate-pulse"></div>
                </div>
              ))}
            </div>
          ) : (
            <div className="grid lg:grid-cols-2 gap-12">
              {/* Left Column - Leadership Section with Scrollable Content */}
              <div className="flex flex-col" style={{ maxHeight: `${leadershipMaxHeight}px` }}>
                <h2 className="text-center text-3xl font-bold mb-6 bg-gradient-to-r from-ministry-gold to-ministry-purple bg-clip-text text-transparent sticky top-0 bg-background z-10 py-4">
                  Leadership
                </h2>
                <div className="flex-1 overflow-y-auto scrollbar-thin space-y-8 pr-2">
                  {/* Executive Committee */}
                  <Collapsible open={ecOpen} onOpenChange={setEcOpen}>
                    <div ref={ecRef}>
                      <CollapsibleTrigger className="w-full flex items-center justify-center gap-2 mb-4 hover:opacity-80 transition-opacity">
                        <h3 className="text-center text-xl font-semibold text-foreground">Executive Committee</h3>
                        {ecOpen ? (
                          <ChevronUp className="w-5 h-5 text-ministry-purple" />
                        ) : (
                          <ChevronDown className="w-5 h-5 text-ministry-purple" />
                        )}
                      </CollapsibleTrigger>
                      <CollapsibleContent>
                        <div className="flex flex-wrap gap-8 justify-center">
                          {currentMembers
                            .filter((m) =>
                              ["president", "vice_president", "secretary", "treasurer", "prayer_secretary"].includes(
                                m.role,
                              ),
                            )
                            .sort((a, b) => {
                              const order = ["president", "vice_president", "secretary", "treasurer", "prayer_secretary"];
                              return order.indexOf(a.role) - order.indexOf(b.role);
                            })
                            .map((member) => (
                              <div key={member.id} className="text-center">
                                <Avatar className="w-16 h-16 mx-auto mb-3">
                                  <AvatarImage
                                    src={member.profile_image_url || ""}
                                    alt={member.full_name}
                                    className="object-cover"
                                  />
                                  <AvatarFallback className="text-2xl bg-gradient-to-br from-ministry-gold to-ministry-purple text-white">
                                    {member.full_name
                                      .split(" ")
                                      .map((n) => n[0])
                                      .join("")}
                                  </AvatarFallback>
                                </Avatar>
                                <h3 className="font-bold text-base mb-1">{member.full_name}</h3>
                                <p className="text-sm text-muted-foreground capitalize">{member.role.replace("_", " ")}</p>
                              </div>
                            ))}
                        </div>
                      </CollapsibleContent>
                    </div>
                  </Collapsible>

                  {/* National Council */}
                  {currentMembers.filter((m) => m.role === "national_council").length > 0 && (
                    <Collapsible open={ncOpen} onOpenChange={setNcOpen}>
                      <div ref={ncRef}>
                        <CollapsibleTrigger className="w-full flex items-center justify-center gap-2 mb-4 hover:opacity-80 transition-opacity">
                          <h3 className="text-center text-xl font-semibold text-foreground">National Council</h3>
                          {ncOpen ? (
                            <ChevronUp className="w-5 h-5 text-primary" />
                          ) : (
                            <ChevronDown className="w-5 h-5 text-primary" />
                          )}
                        </CollapsibleTrigger>
                        <CollapsibleContent>
                          <div className="flex flex-wrap gap-8 justify-center">
                            {currentMembers
                              .filter((m) => m.role === "national_council")
                              .sort((a, b) => a.full_name.localeCompare(b.full_name))
                              .map((member) => (
                                <div key={member.id} className="text-center">
                                  <Avatar className="w-16 h-16 mx-auto mb-3">
                                    <AvatarImage
                                      src={member.profile_image_url || ""}
                                      alt={member.full_name}
                                      className="object-cover"
                                    />
                                    <AvatarFallback className="text-lg bg-primary/10 text-primary">
                                      {member.full_name
                                        .split(" ")
                                        .map((n) => n[0])
                                        .join("")}
                                    </AvatarFallback>
                                  </Avatar>
                                  <h3 className="font-semibold text-base mb-1">{member.full_name}</h3>
                                  <p className="text-muted-foreground text-sm">National Council</p>
                                </div>
                              ))}
                          </div>
                        </CollapsibleContent>
                      </div>
                    </Collapsible>
                  )}

                  {/* Regional Coordinators - Simple layout like Executive Committee */}
                  <Collapsible open={rcOpen} onOpenChange={setRcOpen}>
                    <div ref={rcRef}>
                      <CollapsibleTrigger className="w-full flex items-center justify-center gap-2 mb-8 hover:opacity-80 transition-opacity">
                        <h3 className="text-center text-2xl font-bold bg-gradient-to-r from-ministry-emerald to-ministry-cyan bg-clip-text text-transparent">
                          Regional Coordinators
                        </h3>
                        {rcOpen ? (
                          <ChevronUp className="w-6 h-6 text-ministry-cyan" />
                        ) : (
                          <ChevronDown className="w-6 h-6 text-ministry-cyan" />
                        )}
                      </CollapsibleTrigger>
                      <CollapsibleContent>
                        <div className="space-y-12">
                          {(() => {
                            // Group coordinators by region - normalize region names
                            const regionGroups = coordinators.reduce(
                              (acc, coord) => {
                                // Normalize region name by removing " Region" suffix to match hardcoded array
                                const normalizedRegionName = coord.regionName.replace(/ Region$/i, "");
                                if (!acc[normalizedRegionName]) {
                                  acc[normalizedRegionName] = {
                                    name: normalizedRegionName,
                                    color: (coord as any).regionColor || "#8B5CF6",
                                    coordinators: [],
                                  };
                                }
                                acc[normalizedRegionName].coordinators.push(coord);
                                return acc;
                              },
                              {} as Record<string, { name: string; color: string; coordinators: typeof coordinators }>,
                            );

                            // Ensure all 6 regions are displayed
                            const allRegions = ["Eastern", "Midwest", "Northwest", "Southeast", "Southern", "Southwest"];

                            return allRegions.map((regionName) => {
                              const regionData = regionGroups[regionName] || {
                                name: regionName,
                                color: "#8B5CF6",
                                coordinators: [],
                              };

                              // Sort coordinators by role priority
                              const rolePriority = {
                                regional_president: 1,
                                regional_vice_president: 2,
                                regional_secretary: 3,
                                regional_joint_secretary: 4,
                                regional_treasurer: 5,
                                regional_prayer_secretary: 6,
                              };
                              const sortedCoords = [...regionData.coordinators].sort(
                                (a: any, b: any) =>
                                  (rolePriority[a.role as keyof typeof rolePriority] || 999) -
                                  (rolePriority[b.role as keyof typeof rolePriority] || 999),
                              );

                              return (
                                <div key={regionName} className="space-y-6">
                                  <h4 className="text-2xl font-semibold text-center" style={{ color: regionData.color }}>
                                    {regionName}
                                  </h4>
                                  {sortedCoords.length > 0 ? (
                                    <div className="flex flex-wrap justify-center gap-8">
                                      {sortedCoords.map((coord: any) => (
                                        <div key={coord.id} className="flex flex-col items-center text-center">
                                          <Avatar className="h-16 w-16 mb-2 ring-2 ring-background shadow-lg">
                                            <AvatarImage src={coord.profile_image_url || ""} alt={coord.full_name} />
                                            <AvatarFallback
                                              className="text-lg font-semibold text-white"
                                              style={{ backgroundColor: regionData.color }}
                                            >
                                              {coord.full_name
                                                .split(" ")
                                                .map((n: string) => n[0])
                                                .join("")}
                                            </AvatarFallback>
                                          </Avatar>
                                          <div className="space-y-1">
                                            <h5 className="font-semibold text-lg">{coord.full_name}</h5>
                                            <p className="text-sm text-muted-foreground">
                                              {regionalRoleLabel[coord.role] ||
                                                coord.role.replace(/_/g, " ").replace(/^regional\s/, "Regional ")}
                                            </p>
                                            {coord.email && (
                                              <p className="text-xs text-primary">
                                                <a href={`mailto:${coord.email}`} className="hover:underline">
                                                  {coord.email}
                                                </a>
                                              </p>
                                            )}
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  ) : (
                                    <p className="text-center text-sm text-muted-foreground italic">
                                      Coordinators information coming soon
                                    </p>
                                  )}
                                </div>
                              );
                            });
                          })()}
                        </div>
                      </CollapsibleContent>
                    </div>
                  </Collapsible>
                </div>
              </div>

              {/* Right Column - President's Message */}
              <div className="flex flex-col space-y-6" ref={presidentMessageRef}>
                <div className="text-center">
                  <h2 className="text-3xl font-bold mb-2 bg-clip-text text-black">President's Message</h2>
                  <p className="text-lg text-muted-foreground">A word from our leader</p>
                </div>

                <Card className="divine-card relative z-10 hover:shadow-lg transition-all duration-300">
                  <CardHeader className="pb-0"></CardHeader>
                  <CardContent className="space-y-6">
                    <div className="flex justify-center">
                      <img
                        src={presidentImage || "/lovable-uploads/0485e80a-1029-45c9-9112-4b1efc04b06e.png"}
                        alt={`OFUESI President ${presidentName}`}
                        className="w-32 h-32 rounded-full object-cover shadow-divine border-4 border-gradient-to-r from-ministry-gold to-ministry-purple"
                        loading="lazy"
                      />
                    </div>
                    {loading ? (
                      <div className="animate-pulse space-y-4">
                        <div className="h-4 bg-muted rounded w-3/4 mx-auto"></div>
                        <div className="h-4 bg-muted rounded w-full mx-auto"></div>
                        <div className="h-4 bg-muted rounded w-2/3 mx-auto"></div>
                      </div>
                    ) : presidentMessage ? (
                      <div className="text-lg italic text-center text-muted-foreground prose prose-lg max-w-none">
                        <div dangerouslySetInnerHTML={{ __html: presidentMessage }} />
                      </div>
                    ) : (
                      <>
                        <blockquote className="text-lg italic text-center text-muted-foreground">
                          "Welcome to the official website of the Overseas Friends of Union of Evangelical Students of
                          India (OFUESI)! Serving as the President, I am honored to lead OFUESI as we approach our
                          Silver Jubilee in 2024. I invite you to explore our website, engage with our community, and
                          connect with our regional groups."
                        </blockquote>

                        <blockquote className="text-lg italic text-center text-muted-foreground">
                          "Inspired by the words of Apostle Paul regarding King David in Acts 13:36, let's
                          wholeheartedly serve our Lord and Savior Jesus Christ in this generation. Let's fully commit
                          ourselves to the work of the Lord (2 Corinthians 15:58). Together, we can positively impact
                          and make a difference in the lives of young minds."
                        </blockquote>
                      </>
                    )}

                    <div className="text-center">
                      <p className="font-semibold text-lg bg-gradient-to-r from-primary to-ministry-purple bg-clip-text text-transparent">
                        {presidentName}
                      </p>
                      <p className="text-muted-foreground">President, OFUESI</p>
                      <p className="text-sm text-muted-foreground italic mt-2">"May God richly bless you all!"</p>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Call to Action - NO DUPLICATE PRESIDENT MESSAGE */}
      {/* Call to Action */}
      <section className="py-20 relative">
        <div className="absolute inset-0 bg-gradient-to-br from-ministry-cyan via-primary to-ministry-cyan"></div>
        <div className="absolute inset-0 faith-pattern opacity-20 bg-[#fa0f0ff2]"></div>
        <div className="container mx-auto px-4 text-center relative z-10">
          <div className="max-w-3xl mx-auto text-white space-y-6">
            <h2 className="text-3xl lg:text-4xl font-bold text-white drop-shadow-lg">Join Our Mission</h2>
            <p className="text-xl leading-relaxed text-white">
              Be part of a community that's making a difference in the lives of university students across America and
              supporting ministry in India.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                asChild
                size="lg"
                className="border-white text-white hover:bg-white hover:text-primary hover:border-primary hover:border-2"
              >
                <Link to="/regions">Find Your Region</Link>
              </Button>
              <Button
                asChild
                size="lg"
                variant="outline"
                className="bg-primary text-white border-2 border-primary hover:bg-white hover:text-primary transition-colors duration-300 cursor-pointer"
              >
                <Link to="/contact">Contact Us</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action Section */}
      <section className="py-20 relative faith-pattern bg-muted/30">
        <div className="container mx-auto px-4 text-center">
          <div className="max-w-3xl mx-auto space-y-6">
            <h2 className="text-3xl font-bold">Stay Connected with OFUESI</h2>
            <p className="text-lg text-muted-foreground">
              Join our community and stay updated with our newsletter, share your testimony, or contribute articles.
            </p>
            <Button
              asChild
              size="lg"
              className="border-white text-white hover:bg-white hover:text-primary hover:border-primary hover:border-2"
            >
              <Link to="/keep-in-touch">Keep in Touch</Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
